/**
 * Resolves the effective IANA timezone used to display timeseries x-axis values.
 *
 * @packageDocumentation
 * @module ResolveDisplayTimezone
 * @public
 */

import { record as R } from '@highimpact/data-utils';
import { DEFAULT_CURVE_TIMEZONE } from './curveTimezoneMapping';
import { CurveId, PlotPreferences } from './plotTypes';

// -- type definitions --

export type PlotDisplayMode = 'timeseries' | 'scatter';

// -- helper functions --

/**
 * Returns the browser's IANA timezone as reported by the runtime.
 *
 * @returns The resolved browser timezone identifier.
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Collects visible curve ids from plot preferences.
 *
 * Curves with `visible === false` are excluded.
 *
 * @param prefs - Plot preferences containing curve metadata.
 * @returns Visible curve ids in stable order.
 */
export function getVisibleCurveIds(prefs: PlotPreferences): readonly CurveId[] {
  const orderMap = R.fromArray(
    prefs.curveOrder?.map((cId, index) => [cId, index]) ?? [],
  );
  return R.keys(
    R.filter((cp) => cp.visible ?? true, prefs.curvePrefs)
  ).sort((a, b) => orderMap[a] - orderMap[b]);
}

/**
 * Returns the mapped timezone for a curve, applying the per-curve default when absent.
 *
 * @param prefs - Plot preferences containing `curveToTimezone`.
 * @param curveId - Target curve id.
 * @returns The mapped IANA timezone for comparison purposes.
 */
export function getMappedCurveTimezone(
  prefs: PlotPreferences,
  curveId: CurveId
): string {
  return prefs.curveToTimezone?.[curveId] ?? DEFAULT_CURVE_TIMEZONE;
}

/**
 * Determines whether any visible curve has explicit (defined) timezone mapping.
 *
 * Used to preserve legacy behavior when no timezone metadata was ever stored.
 *
 * @param prefs - Plot preferences to inspect.
 * @param visibleCurveIds - Visible curves currently rendered on the plot.
 * @returns `true` when at least one visible curve has an explicit timezone entry.
 */
export function hasExplicitTimezoneMapping(
  prefs: PlotPreferences,
): boolean {
  const visibleCurveIds = getVisibleCurveIds(prefs);
  return visibleCurveIds.some(
    (cId) => prefs.curveToTimezone?.[cId] !== undefined
  );
}

/**
 * Collects mapped timezones for all visible curves.
 *
 * @param prefs - Plot preferences containing `curveToTimezone`.
 * @param visibleCurveIds - Visible curves currently rendered on the plot.
 * @returns One mapped timezone string per visible curve.
 */
export function collectMappedCurveTimezones(
  prefs: PlotPreferences,
): readonly string[] {
  const visibleCurveIds = getVisibleCurveIds(prefs);
  return visibleCurveIds.map((cId) => getMappedCurveTimezone(prefs, cId));
}

/**
 * Returns `true` when every mapped timezone value is identical.
 *
 * @param mappedTimezones - Timezone values collected from visible curves.
 * @returns Whether all values refer to the same IANA timezone.
 */
export function areAllMappedTimezonesEqual(
  mappedTimezones: readonly string[]
): boolean {
  if (mappedTimezones.length < 2) return true;
  return mappedTimezones.every((tz) => tz === mappedTimezones[0]);
}

/**
 * Returns the sole timezone when all mapped values are equal.
 *
 * @param mappedTimezones - Timezone values collected from visible curves.
 * @returns The unified timezone.
 * @throws Error when the input does not describe exactly one unique timezone.
 */
export function getUnifiedMappedTimezone(
  mappedTimezones: readonly string[]
): string {
  if (mappedTimezones.length === 0) {
    throw new Error('No mapped timezones provided');
  }
  if (!areAllMappedTimezonesEqual(mappedTimezones)) {
    throw new Error('Mapped timezones are not equal');
  }
  return mappedTimezones[0];
}

// -- main --

/**
 * Resolves the timezone used to render timeseries axis labels, hovers, and range interactions.
 *
 * Rules:
 * - Scatter mode always returns the browser timezone.
 * - Timeseries with no explicit timezone mapping falls back to the browser timezone.
 * - Timeseries with a single mapped timezone across all visible curves uses that timezone.
 * - Otherwise falls back to the browser timezone.
 *
 * @param prefs - Plot preferences containing per-curve timezone mappings.
 * @param plotType - Current plot display mode.
 * @returns The IANA timezone identifier to use for display.
 */
export function resolveDisplayTimezone(
  prefs: PlotPreferences,
  plotType: PlotDisplayMode
): string {
  if (plotType === 'scatter') {
    return getBrowserTimezone();
  }

  if (!hasExplicitTimezoneMapping(prefs)) {
    return getBrowserTimezone();
  }

  const timezones = collectMappedCurveTimezones(prefs);
  if (timezones.length === 0) {
    return getBrowserTimezone();
  }

  if (areAllMappedTimezonesEqual(timezones)) {
    return getUnifiedMappedTimezone(timezones);
  }

  return getBrowserTimezone();
}
